// Regras puras da cruzada (contrato P0): pontuação, revelação por casa e formato
// do cache. Sem React, sem HTTP — o reducer e a tela só chamam estas funções.
import { finishTempo } from '../core/session/time';
import type { iGameTemplate, iSquare, iSquareSaved } from './context/interfaces';

export type Orientation = 'horizontal' | 'vertical';

export interface CellPos {
  row: number;
  col: number;
}

/** Corpo de POST /passatempos/:id/progresso. */
export interface CrosswordCache {
  tempo: number;
  pontuacao: number;
  matrix: iSquareSaved[][];
}

/** Corpo de POST /passatempos/:id/verificar. */
export interface VerifyRequest {
  matrix: iSquare[][];
  tempo: number;
  pontuacao: number;
}

/** Cache que o back grava depois do verificar. */
export interface VerifiedCache {
  tempo: number;
  acertos: number;
  total: number;
  matrix?: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const upper = (value: unknown): string => (typeof value === 'string' ? value.toUpperCase() : '');

/** Casa jogável = tem palavra em alguma direção (mesma regra do servidor). */
export function isPlayable(cell: iSquare | undefined): boolean {
  return !!cell && !(cell.palavra_horizontal === -1 && cell.palavra_vertical === -1);
}

export function allPlayableCells(template: iGameTemplate): CellPos[] {
  const cells: CellPos[] = [];
  template.matrix.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (isPlayable(cell)) cells.push({ row: r, col: c });
    })
  );
  return cells;
}

export function maxCrosswordScore(template: iGameTemplate): number {
  return allPlayableCells(template).length;
}

/** Grade no formato do cache, preenchida com o que estiver salvo (leitura tolerante). */
export function mergeSavedMatrix(template: iGameTemplate, saved: unknown): iSquareSaved[][] {
  const rows: unknown[] = Array.isArray(saved) ? saved : [];
  return template.matrix.map((row, r) =>
    row.map((cell, c): iSquareSaved => {
      const savedRow = rows[r];
      const savedCell: unknown = Array.isArray(savedRow) ? savedRow[c] : undefined;
      const base: iSquareSaved = {
        numero: cell.numero,
        letra: isRecord(savedCell) ? upper(savedCell.letra) : '',
        palavraHorizontal: cell.palavra_horizontal,
        palavraVertical: cell.palavra_vertical,
      };
      return isRecord(savedCell) && savedCell.revelada === true ? { ...base, revelada: true } : base;
    })
  );
}

function countMatches(template: iGameTemplate, matrix: iSquareSaved[][], includeRevealed: boolean): number {
  let total = 0;
  template.matrix.forEach((row, r) =>
    row.forEach((cell, c) => {
      if (!isPlayable(cell)) return;
      const mine = matrix[r]?.[c];
      if (!mine || (mine.revelada && !includeRevealed)) return;
      const letra = upper(mine.letra);
      if (letra !== '' && letra === upper(cell.letra)) total += 1;
    })
  );
  return total;
}

/** Contrato: casas jogáveis com letra certa (case-insensitive) E sem revelada. */
export function scoreCrossword(template: iGameTemplate, matrix: iSquareSaved[][]): number {
  return countMatches(template, matrix, false);
}

/** Acertos mostrados no fim (inclui as casas reveladas). */
export function countCorrectCrossword(template: iGameTemplate, matrix: iSquareSaved[][]): number {
  return countMatches(template, matrix, true);
}

export function revealCells(template: iGameTemplate, matrix: iSquareSaved[][], cells: CellPos[]): iSquareSaved[][] {
  const next = matrix.map((row) => row.map((cell) => ({ ...cell })));
  cells.forEach(({ row, col }) => {
    const answer = template.matrix[row]?.[col];
    const mine = next[row]?.[col];
    if (!answer || !mine || !isPlayable(answer)) return;
    next[row][col] = { ...mine, letra: upper(answer.letra), revelada: true };
  });
  return next;
}

/** Casas da palavra da chave (`fim` = tamanho − 1, inclusive). */
export function wordCells(template: iGameTemplate, orientation: Orientation, clueNumber: string): CellPos[] {
  const clue = template.chaves[orientation]?.[clueNumber]?.[0];
  if (!clue || !clue.palavras) return [];
  const { x, y, fim } = clue.palavras;
  const length = (typeof fim === 'number' ? fim : -1) + 1;
  const cells: CellPos[] = [];
  for (let i = 0; i < length; i++) {
    const pos = orientation === 'horizontal' ? { row: y, col: x + i } : { row: y + i, col: x };
    if (isPlayable(template.matrix[pos.row]?.[pos.col])) cells.push(pos);
  }
  return cells;
}

/** "Limpar jogo": apaga o que o jogador digitou; casas reveladas continuam reveladas. */
export function clearLetters(matrix: iSquareSaved[][]): iSquareSaved[][] {
  return matrix.map((row) => row.map((cell) => (cell.revelada ? { ...cell } : { ...cell, letra: '' })));
}

/** Gabarito com as letras DO JOGADOR (o servidor pontua casa a casa). */
export function toVerifyMatrix(template: iGameTemplate, matrix: iSquareSaved[][]): iSquare[][] {
  return template.matrix.map((row, r) => row.map((cell, c) => ({ ...cell, letra: upper(matrix[r]?.[c]?.letra) })));
}

export function buildCrosswordCache(template: iGameTemplate, matrix: iSquareSaved[][], seconds: number): CrosswordCache {
  return { tempo: Math.max(0, Math.round(seconds)), pontuacao: scoreCrossword(template, matrix), matrix };
}

export function buildVerifyRequest(template: iGameTemplate, matrix: iSquareSaved[][], seconds: number): VerifyRequest {
  return {
    matrix: toVerifyMatrix(template, matrix),
    tempo: finishTempo(seconds),
    pontuacao: scoreCrossword(template, matrix),
  };
}

export function isVerifiedCache(cache: unknown): cache is VerifiedCache {
  return isRecord(cache) && typeof cache.acertos === 'number' && typeof cache.total === 'number';
}
